import React, { useEffect, useState } from "react";
import { reportsApi, fmtUSD } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { toast } from "sonner";

const err = (e) => e?.response?.data?.detail || e.message;

export function CreditsPanel() {
  const [data, setData] = useState(null);
  const load = () => reportsApi.credits().then(setData).catch(console.error);
  useEffect(() => { load(); }, []);
  if (!data) return <div className="text-sm text-muted-foreground py-6">Loading credits…</div>;

  const assign = async (id, accountId) => {
    try {
      await reportsApi.assignCredit(id, accountId === "unassigned" ? null : accountId);
      toast.success("Credit assignment updated");
      load();
    } catch (e) { toast.error("Failed", { description: err(e) }); }
  };

  return (
    <div className="space-y-4" data-testid="credits-panel">
      <div className="grid grid-cols-3 gap-3">
        {[["Granted", data.credits.reduce((s, c) => s + c.amount, 0)],
          ["Applied", data.credits.reduce((s, c) => s + c.applied, 0)],
          ["Remaining", data.credits.reduce((s, c) => s + c.remaining, 0)]].map(([l, v]) => (
          <div key={l} className="cs-card p-4">
            <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">{l}</div>
            <div className="cs-mono text-lg font-semibold mt-1">{fmtUSD(v)}</div>
          </div>
        ))}
      </div>
      <div className="cs-card overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-[hsl(var(--cs-tint))] border-b border-border">
            <tr className="text-left">
              {["Credit", "Program", "Granted", "Applied", "Remaining", "Expires", "Assigned account"].map((h) => (
                <th key={h} className="px-3 py-2 text-[10px] uppercase tracking-[0.12em] text-muted-foreground">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.credits.map((c) => (
              <tr key={c.id} className="border-b border-border last:border-0" data-testid={`credit-${c.id}`}>
                <td className="px-3 py-2 font-medium">{c.name}</td>
                <td className="px-3 py-2 text-muted-foreground">{c.program}</td>
                <td className="px-3 py-2 text-right cs-mono">{fmtUSD(c.amount)}</td>
                <td className="px-3 py-2 text-right cs-mono">{fmtUSD(c.applied)}</td>
                <td className="px-3 py-2 text-right cs-mono font-semibold text-[#15803d]">{fmtUSD(c.remaining)}</td>
                <td className="px-3 py-2 cs-mono text-muted-foreground">{c.expires}</td>
                <td className="px-3 py-2">
                  <Select value={c.account_id || "unassigned"} onValueChange={(v) => assign(c.id, v)}>
                    <SelectTrigger className="rounded-sm h-7 text-[11px] w-[190px]" data-testid={`assign-credit-${c.id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unassigned">Unassigned (payer pool)</SelectItem>
                      {data.accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} ({a.id.slice(0, 4)}…)</SelectItem>)}
                    </SelectContent>
                  </Select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-[11px] text-muted-foreground">
        Unassigned credits apply at the payer level and drain in invoice order; assigned credits are reserved for that account's charges only.
      </div>
    </div>
  );
}

export function MemosPanel() {
  const [memos, setMemos] = useState([]);
  const [form, setForm] = useState({ scope: "account", ref: "", text: "" });
  const load = () => reportsApi.memos().then(setMemos).catch(console.error);
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.text.trim()) { toast.error("Memo text is required"); return; }
    try {
      await reportsApi.addMemo(form);
      toast.success("Memo added");
      setForm({ scope: "account", ref: "", text: "" });
      load();
    } catch (e) { toast.error("Failed", { description: err(e) }); }
  };

  return (
    <div className="space-y-4" data-testid="memos-panel">
      <div className="cs-card p-4 space-y-2">
        <div className="text-xs font-semibold">New memo</div>
        <div className="flex gap-2">
          <Select value={form.scope} onValueChange={(v) => setForm({ ...form, scope: v })}>
            <SelectTrigger className="rounded-sm h-9 text-xs w-[130px]" data-testid="memo-scope-select"><SelectValue /></SelectTrigger>
            <SelectContent>{["account", "invoice", "family"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <Input value={form.ref} onChange={(e) => setForm({ ...form, ref: e.target.value })}
            placeholder="Reference (account / invoice number)" data-testid="memo-ref-input" className="rounded-sm h-9 text-xs" />
        </div>
        <Textarea value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })}
          placeholder="Memo text shown alongside billing data…" data-testid="memo-text-input" className="rounded-sm text-xs" rows={2} />
        <Button onClick={add} data-testid="add-memo-button" className="rounded-full h-8 text-xs bg-[#00579e] hover:bg-[#004580] text-white">Add memo</Button>
      </div>
      {memos.map((m) => (
        <div key={m.id} className="cs-card p-4 flex items-start gap-3" data-testid={`memo-${m.id}`}>
          <span className="px-1.5 py-0.5 rounded-sm text-[10px] uppercase font-semibold bg-[#e0f2fe] text-[#0369a1]">{m.scope}</span>
          <div className="flex-1">
            <div className="text-[11px] cs-mono text-muted-foreground">{m.ref} · {m.author} · {(m.created_at || "").slice(0, 10)}</div>
            <div className="text-xs mt-1">{m.text}</div>
          </div>
          <button onClick={async () => { await reportsApi.deleteMemo(m.id); load(); }}
            data-testid={`delete-memo-${m.id}`} className="text-[11px] text-[#b91c1c] hover:underline">delete</button>
        </div>
      ))}
    </div>
  );
}

export function RatesPanel() {
  const [rates, setRates] = useState([]);
  const [form, setForm] = useState({ provider: "AWS", service: "", unit: "", rate_type: "monthly", rate: "" });
  const load = () => reportsApi.rates().then(setRates).catch(console.error);
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.service || !form.rate) { toast.error("Service and rate are required"); return; }
    try {
      await reportsApi.addRate({ ...form, rate: parseFloat(form.rate) });
      toast.success("Custom usage rate created — applied at next rating run");
      setForm({ provider: "AWS", service: "", unit: "", rate_type: "monthly", rate: "" });
      load();
    } catch (e) { toast.error("Failed", { description: err(e) }); }
  };

  return (
    <div className="space-y-4" data-testid="rates-panel">
      <div className="cs-card p-4">
        <div className="text-xs font-semibold mb-2">New custom usage rate (overrides the provider rate for rebilling)</div>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
          <Select value={form.provider} onValueChange={(v) => setForm({ ...form, provider: v })}>
            <SelectTrigger className="rounded-sm h-9 text-xs" data-testid="rate-provider-select"><SelectValue /></SelectTrigger>
            <SelectContent>{["AWS", "GCP", "Azure", "OCI"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
          </Select>
          <Input value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })}
            placeholder="Service (e.g. EC2)" data-testid="rate-service-input" className="rounded-sm h-9 text-xs" />
          <Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })}
            placeholder="Unit (e.g. instance-hour)" data-testid="rate-unit-input" className="rounded-sm h-9 text-xs" />
          <Select value={form.rate_type} onValueChange={(v) => setForm({ ...form, rate_type: v })}>
            <SelectTrigger className="rounded-sm h-9 text-xs" data-testid="rate-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>{["monthly", "hourly"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
          <Input value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })} type="number"
            placeholder={form.rate_type === "hourly" ? "$/hr" : "$/mo"} data-testid="rate-value-input" className="rounded-sm h-9 text-xs" />
          <Button onClick={add} data-testid="add-rate-button" className="rounded-full h-9 text-xs bg-[#00579e] hover:bg-[#004580] text-white">Add rate</Button>
        </div>
      </div>
      <div className="cs-card overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-[hsl(var(--cs-tint))] border-b border-border">
            <tr className="text-left">
              {["Provider", "Service", "Unit", "Type", "Rate", "Standard/mo", "Custom/mo", "Δ effect", ""].map((h) => (
                <th key={h} className="px-3 py-2 text-[10px] uppercase tracking-[0.12em] text-muted-foreground">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rates.length === 0 && <tr><td colSpan={9} className="px-3 py-8 text-center text-muted-foreground" data-testid="no-rates">No custom rates yet.</td></tr>}
            {rates.map((r) => (
              <tr key={r.id} className="border-b border-border last:border-0" data-testid={`rate-${r.id}`}>
                <td className="px-3 py-2 cs-mono">{r.provider}</td>
                <td className="px-3 py-2 font-medium">{r.service}</td>
                <td className="px-3 py-2 text-muted-foreground">{r.unit || "—"}</td>
                <td className="px-3 py-2">{r.rate_type}</td>
                <td className="px-3 py-2 text-right cs-mono">{fmtUSD(r.rate)}{r.rate_type === "hourly" ? "/hr" : "/mo"}</td>
                <td className="px-3 py-2 text-right cs-mono text-muted-foreground">{fmtUSD(r.effect_preview?.standard_monthly)}</td>
                <td className="px-3 py-2 text-right cs-mono">{fmtUSD(r.effect_preview?.custom_monthly)}</td>
                <td className={`px-3 py-2 text-right cs-mono font-semibold ${r.effect_preview?.delta < 0 ? "text-[#15803d]" : "text-[#b45309]"}`}>
                  {r.effect_preview?.delta > 0 ? "+" : ""}{fmtUSD(r.effect_preview?.delta)}
                </td>
                <td className="px-3 py-2 text-right">
                  <button onClick={async () => { await reportsApi.deleteRate(r.id); load(); }}
                    data-testid={`delete-rate-${r.id}`} className="text-[11px] text-[#b91c1c] hover:underline">delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
